import { Fragment, useState } from 'react';
import { Link } from 'react-router';
import { useMeta } from '@/client/lib/useMeta';
import { api } from '@/client/lib/api';
import { track } from '@/client/lib/analytics';
import { SectionHeader } from '@/components/SectionHeader';
import { FramedImage } from '@/components/FramedImage';
import { Turnstile, TURNSTILE_ENABLED } from '@/components/Turnstile';
import { BRAND, SERVICE_AREA } from '@/config/site';
import { Newsletter } from '@/features/newsletter/Newsletter';
import { useI18n } from '@/client/i18n/I18nContext';
import { Markdown } from '@/components/Markdown';
import { staticPageText } from '@/lib/seo';
import { SERVICE_GROUPS, serviceListItems, servicePath, type ServiceDef } from '@/content/services';
import { translate } from '@/client/i18n/dictionary';
import { STUDIO_PATH } from '@/lib/colorimetry/route';
import { GamePromo } from '@/components/GamePromo';
import { lp } from '@/client/i18n/locale';
import { ContactSourceField } from '@/components/ContactSourceField';
import { MESSAGE_MAX_LENGTH, withSource } from '@/lib/contactSource';

export function AboutPage() {
  const { t, locale } = useI18n();
  useMeta({ ...staticPageText('/caro', locale), path: '/caro' });
  return (
    <div className="container">
      {/* JSON-LD (OnlineStore + ProfilePage) is injected by the Worker into
          <head> (pageMeta /caro): not repeated here. */}
      <div className="pdp" style={{ paddingBlock: 'var(--space-6)', alignItems: 'start' }}>
        <div className="casa-card__media" style={{ aspectRatio: '4 / 5' }}>
          <FramedImage src="/media/caro/carolina-collaer-caro-casa-de-estilo-portada.webp" alt={t('about.portrait')} fallback="CARO" />
        </div>
        <div className="stack">
          <p className="eyebrow">{t('about.eyebrow')}</p>
          <h1>{t('about.title')}</h1>
          <p className="lede">{t('about.lede')}</p>
          <p>{t('about.story1')}</p>
          <p>{t('about.story2')}</p>
          <p>{t('about.story3')}</p>
          <p className="text-muted" style={{ fontSize: 'var(--fs-sm)', marginTop: 'var(--space-2)' }}>
            {t('about.tags')}
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginTop: 'var(--space-2)' }}>
            <Link className="btn" to={lp('/caro/servicios')}>{t('nav.services')}</Link>
            <a className="btn btn--ghost" href={BRAND.instagramUrl} target="_blank" rel="noopener noreferrer">
              {t('about.followIg')}
            </a>
          </div>
        </div>
      </div>

      <Newsletter />
    </div>
  );
}

export function PhilosophyPage() {
  const { t, locale } = useI18n();
  useMeta({ ...staticPageText('/caro/filosofia', locale), path: '/caro/filosofia' });
  return (
    <div className="container">
      <SectionHeader as="h1" eyebrow={t('nav.caro')} title={t('philosophy.title')} lede={t('philosophy.lede')} />
      <div className="prose" style={{ paddingBottom: 'var(--space-8)' }}>
        <Markdown source={t('philosophy.body')} />
      </div>
    </div>
  );
}

/** A service: name, price, what it includes. On /caro/servicios it links to its own page (`withLink`). */
export function ServiceCard({ svc, withLink = false, as: Title = 'h3' }: { svc: ServiceDef; withLink?: boolean; as?: 'h2' | 'h3' }) {
  const { t } = useI18n();
  const p = (suffix: string) => t(`services.svc.${svc.key}.${suffix}`);
  const list = svc.listAfter ? serviceListItems(p('list')) : [];
  return (
    <article className="spec" style={{ borderTop: 'var(--border-hairline)', paddingBlock: 'var(--space-4)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Title style={{ margin: 0 }}>{p('name')}</Title>
        <span className="text-muted" style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
          {p('price')}{svc.meta ? ` · ${p('meta')}` : ''}
        </span>
      </div>
      <div className="stack" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-3)', maxWidth: 'var(--width-text)' }}>
        {svc.bodies.map((n) => (
          <Fragment key={n}>
            <p className="text-muted">{p(`body${n}`)}</p>
            {svc.listAfter === n ? (
              <div>
                {svc.listTitle ? <p style={{ margin: 0, fontWeight: 500 }}>{p('listTitle')}</p> : null}
                <ul className="text-muted" style={{ margin: '0.25rem 0 0', paddingLeft: '1.1rem' }}>
                  {list.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            ) : null}
          </Fragment>
        ))}
        {svc.note ? (
          <p className="text-muted" style={{ fontSize: 'var(--fs-sm)', fontStyle: 'italic' }}>{p('note')}</p>
        ) : null}
        {withLink ? (
          <p style={{ margin: 0 }}>
            <Link to={lp(servicePath(svc.key))}>{t('services.page.more')}: {p('name')}</Link>
          </p>
        ) : null}
      </div>
    </article>
  );
}

export function ServicesPage() {
  const { t, locale } = useI18n();
  const [tab, setTab] = useState<'personal' | 'pro'>('personal');
  useMeta({ ...staticPageText('/caro/servicios', locale), path: '/caro/servicios' });
  const group = SERVICE_GROUPS.find((g) => g.key === tab)!;
  return (
    <div className="container">
      <SectionHeader as="h1" eyebrow={t('services.eyebrow')} title={t('services.title')} lede={t('services.lede')} />

      {/* Manifesto + portrait */}
      <div className="pdp" style={{ alignItems: 'start', paddingBottom: 'var(--space-6)' }}>
        <div className="casa-card__media" style={{ aspectRatio: '4 / 5' }}>
          <FramedImage src="/media/caro/asesoria-de-imagen-carolina-collaer-dior-paris.webp" alt={t('services.imageAlt')} fallback="CARO" />
        </div>
        <div className="stack" style={{ maxWidth: 'var(--width-text)' }}>
          <p>{t('services.manifesto1')}</p>
          <p>{t('services.manifesto2')}</p>
          <p>{t('services.manifesto3')}</p>
          <p>{t('services.manifesto4')}</p>
          <p className="lede" style={{ marginTop: 'var(--space-2)' }}>{t('services.mantra')}</p>
        </div>
      </div>

      {/* Dónde trabajo: zones from SERVICE_AREA (same block in the prerender) */}
      <div className="stack" style={{ maxWidth: 'var(--width-text)', paddingBottom: 'var(--space-6)' }}>
        <p className="eyebrow">{t('services.area.eyebrow')}</p>
        <p>{t('services.area.body')}</p>
        <p>{SERVICE_AREA.map((z) => z.name).join(' · ')}</p>
        <p className="text-muted">{t('services.area.travel')}</p>
        <p className="text-muted">{t('common.hours')}</p>
      </div>

      <GamePromo game="colorimetry" />
      <p style={{ maxWidth: 'var(--width-text)', paddingBottom: 'var(--space-6)' }}>
        <Link to={lp(STUDIO_PATH)}>{t('services.testsLink')}</Link>
      </p>

      {/* Services in sub-tabs (subpestañas) */}
      <section style={{ paddingBottom: 'var(--space-6)' }}>
        <div className="subtabs" role="tablist" aria-label={t('services.tabs.label')}>
          <button
            id="svc-tab-personal"
            role="tab"
            aria-selected={tab === 'personal'}
            aria-controls="svc-tabpanel"
            className={tab === 'personal' ? 'is-active' : ''}
            onClick={() => setTab('personal')}
          >
            {t('services.tabs.personal')}
          </button>
          <button
            id="svc-tab-pro"
            role="tab"
            aria-selected={tab === 'pro'}
            aria-controls="svc-tabpanel"
            className={tab === 'pro' ? 'is-active' : ''}
            onClick={() => setTab('pro')}
          >
            {t('services.tabs.pro')}
          </button>
        </div>

        <div id="svc-tabpanel" role="tabpanel" aria-labelledby={`svc-tab-${tab}`}>
          {tab === 'pro' ? (
            <p className="lede" style={{ maxWidth: 'var(--width-text)', marginBottom: 'var(--space-3)' }}>
              {t('services.tabs.proLede')}
            </p>
          ) : null}

          <div className="stack" style={{ gap: 0 }}>
            {group.services.map((svc) => (
              <ServiceCard key={svc.key} svc={svc} withLink />
            ))}
          </div>

          <p className="text-muted" style={{ fontSize: 'var(--fs-sm)', marginTop: 'var(--space-4)', maxWidth: 'var(--width-text)' }}>
            {t('services.finePrint')}
          </p>
        </div>
      </section>

      {/* Contact form — reaches Caro */}
      <div style={{ paddingBottom: 'var(--space-8)' }}>
        <p className="eyebrow">{t('services.formTitle')}</p>
        <ServiceEnquiryForm />
      </div>
    </div>
  );
}

export function ServiceEnquiryForm({ service }: { service?: string } = {}) {
  const { t, locale } = useI18n();
  const [form, setForm] = useState({ name: '', email: '', message: '', company: '', source: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [tsToken, setTsToken] = useState('');
  const [tsReset, setTsReset] = useState(0);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (TURNSTILE_ENABLED && !tsToken) {
      setStatus('error');
      return;
    }
    setStatus('loading');
    try {
      const { source, ...fields } = form;
      await api.contact({
        ...fields,
        // Tags for the owner's inbox: stay in Spanish whatever the site locale.
        message: withSource(`[Asesoría de imagen${service ? ` · ${translate('es', `services.svc.${service}.name`)}` : ''}] ${form.message}`, source),
        locale,
        turnstileToken: tsToken,
      });
      track('contact', source ? { kind: 'services', source } : { kind: 'services' });
      setStatus('done');
    } catch {
      setStatus('error');
      setTsReset((n) => n + 1);
    }
  }

  if (status === 'done') {
    return (
      <div className="caro-note" style={{ marginTop: 'var(--space-3)' }}>
        <p className="caro-note__body">{t('services.formThanks')}</p>
      </div>
    );
  }

  return (
    <form className="stack" style={{ marginTop: 'var(--space-3)', maxWidth: 'var(--width-text)' }} onSubmit={onSubmit}>
      <div className="form-grid form-grid--2">
        <div className="field">
          <label htmlFor="sv-name">{t('common.name')}</label>
          <input id="sv-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="sv-email">{t('common.email')}</label>
          <input id="sv-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
      </div>
      <div className="field">
        <label htmlFor="sv-msg">{t('common.message')}</label>
        <textarea id="sv-msg" rows={4} required maxLength={MESSAGE_MAX_LENGTH} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
      </div>
      <ContactSourceField id="sv-source" value={form.source} onChange={(source) => setForm({ ...form, source })} />
      <div className="honeypot" aria-hidden>
        <input tabIndex={-1} autoComplete="off" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
      </div>
      <Turnstile onVerify={setTsToken} resetSignal={tsReset} />
      <button type="submit" className="btn" disabled={status === 'loading'}>
        {status === 'loading' ? t('common.sending') : t('services.formCta')}
      </button>
      {status === 'error' ? <p style={{ color: 'var(--color-danger)' }}>{t('common.sendError')}</p> : null}
    </form>
  );
}

